// RF Admin API client — 全 cookie-only,HttpOnly Cookie Only 模型(P2-25)
//
// 全部 endpoint 都用 credentials: "include" 自动带 cookie。

import { rfAdminFetch, type RfAdminUser } from "../api/client";

// ────────────────────── Tenants ──────────────────────

export interface RfTenant {
  id: number;
  postgres_tenant_id: string;
  company_name: string;
  contract_type: string;
  contract_start: string;
  contract_end: string | null;
  features: string[];
  status: "active" | "suspended" | "deleted";
  created_at: string;
}

export interface RfTenantCreateResult {
  postgres_tenant_id: string;
  postgres_user_id: string;
  tenant_code: string;
  rf_tenant_id: number;
  rf_user_id: number;
  admin_email: string;
  tenant_name: string;
}

export async function rfAdminListTenants(): Promise<RfTenant[]> {
  const resp = await rfAdminFetch("/api/rf-admin/tenants", { credentials: "include" });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "list failed");
  return body.data?.tenants || [];
}

export async function rfAdminCreateTenant(payload: {
  company_name: string;
  admin_email: string;
  admin_password: string;
  contract_type?: string;
  contract_start?: string;
  contract_end?: string | null;
  features?: string[];
}): Promise<RfTenantCreateResult> {
  const resp = await rfAdminFetch("/api/rf-admin/tenants", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const body = await resp.json();
  if (!body.success) {
    throw new Error(`${body.error?.code || "ERR"}: ${body.error?.message || "create failed"}`);
  }
  return body.data;
}

export async function rfAdminGetTenant(id: number): Promise<RfTenant> {
  const resp = await rfAdminFetch(`/api/rf-admin/tenants/${id}`, { credentials: "include" });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "get failed");
  return body.data.tenant;
}

export async function rfAdminUpdateTenant(
  id: number,
  patch: Partial<{
    contract_type: string;
    contract_start: string;
    contract_end: string | null;
    features: string[];
    status: "active" | "suspended" | "deleted";
  }>,
): Promise<RfTenant> {
  const resp = await rfAdminFetch(`/api/rf-admin/tenants/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "update failed");
  return body.data.tenant;
}

export async function rfAdminDeleteTenant(id: number): Promise<RfTenant> {
  // M5:软删 = PATCH status=deleted
  return rfAdminUpdateTenant(id, { status: "deleted" });
}

// ────────────────────── Users ──────────────────────

export interface RfUser {
  id: number;
  rf_tenant_id: number;
  postgres_user_id: string;
  email: string;
  status: "invited" | "active" | "disabled";
  last_login_at: string | null;
  created_at: string;
  // 2026-09-19:Role 从 Postgres JOIN public.user_role_assignments + roles 实时查,
  // 不存 SQLite mirror。没 assignment 时 = null(M5 之前创建的 user 可能没绑 role)。
  role_code?: RfRoleCode | null;
}

export type RfRoleCode = "system_admin" | "editor" | "viewer";

export async function rfAdminListTenantUsers(tenantId: number): Promise<RfUser[]> {
  const resp = await rfAdminFetch(`/api/rf-admin/tenants/${tenantId}/users`, { credentials: "include" });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "list failed");
  return body.data?.users || [];
}

export async function rfAdminSendSetup(tenantId: number, userId: number): Promise<{ mail_log_id: number; token_expires_at: string }> {
  const resp = await rfAdminFetch(
    `/api/rf-admin/tenants/${tenantId}/users/${userId}/send-setup`,
    { method: "POST", credentials: "include" },
  );
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "send-setup failed");
  return body.data;
}

export async function rfAdminResendSetup(userId: number): Promise<{ mail_log_id: number; token_expires_at: string }> {
  const resp = await rfAdminFetch(`/api/rf-admin/users/${userId}/resend-setup`, {
    method: "POST",
    credentials: "include",
  });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "resend failed");
  return body.data;
}

export async function rfAdminCreateUser(
  tenantId: number,
  payload: { email: string; role_code: RfRoleCode; password?: string },
): Promise<{ rf_user_id: number; postgres_user_id: string; email: string; password: string; role_code: RfRoleCode }> {
  const resp = await rfAdminFetch(`/api/rf-admin/tenants/${tenantId}/users/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const body = await resp.json();
  if (!body.success) {
    throw new Error(`${body.error?.code || "ERR"}: ${body.error?.message || "create user failed"}`);
  }
  return body.data;
}

// 2026-09-19:旧 invite_user 函数语义已统一到 create_user(默认密码 + active + 无邮件)。
// 保留名字只为兼容老的 import;行为与 createUser 完全一致。
export const rfAdminInviteUser = rfAdminCreateUser;

export async function rfAdminEditUser(
  userId: number,
  patch: Partial<{
    email: string;
    role_code: RfRoleCode;
    status: "invited" | "active" | "disabled";
  }>,
): Promise<RfUser> {
  const resp = await rfAdminFetch(`/api/rf-admin/users/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  const body = await resp.json();
  if (!body.success) {
    throw new Error(`${body.error?.code || "ERR"}: ${body.error?.message || "update failed"}`);
  }
  return body.data.user;
}

export async function rfAdminUpdateUserStatus(
  userId: number,
  status: "invited" | "active" | "disabled",
): Promise<RfUser> {
  return rfAdminEditUser(userId, { status });
}

// ────────────────────── Admin management (super only) ──────────────────────

export async function rfAdminListAdmins(): Promise<RfAdminUser[]> {
  const resp = await rfAdminFetch("/api/rf-admin/admins", { credentials: "include" });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "list failed");
  return body.data?.admins || [];
}

export async function rfAdminCreateAdmin(payload: {
  email: string;
  password: string;
  role: "super" | "sub";
}): Promise<RfAdminUser> {
  const resp = await rfAdminFetch("/api/rf-admin/admins", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "create failed");
  return body.data.admin;
}

export async function rfAdminUpdateAdminStatus(
  id: number,
  status: "active" | "disabled",
): Promise<RfAdminUser> {
  return rfAdminEditAdmin(id, { status });
}

export async function rfAdminEditAdmin(
  id: number,
  patch: Partial<{
    email: string;
    role: "super" | "sub";
    status: "active" | "disabled";
  }>,
): Promise<RfAdminUser> {
  const resp = await rfAdminFetch(`/api/rf-admin/admins/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  const body = await resp.json();
  if (!body.success) {
    throw new Error(`${body.error?.code || "ERR"}: ${body.error?.message || "update failed"}`);
  }
  return body.data.admin;
}

export async function rfAdminResetAdminPassword(
  id: number,
  new_password: string,
): Promise<void> {
  const resp = await rfAdminFetch(`/api/rf-admin/admins/${id}/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ new_password }),
  });
  const body = await resp.json();
  if (!body.success) {
    throw new Error(`${body.error?.code || "ERR"}: ${body.error?.message || "reset failed"}`);
  }
}

export async function rfAdminChangeMyPassword(
  old_password: string,
  new_password: string,
): Promise<void> {
  const resp = await rfAdminFetch("/api/rf-admin/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ old_password, new_password }),
  });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "change failed");
}

export async function rfAdminResetUserPassword(
  userId: number,
  new_password: string,
): Promise<void> {
  const resp = await rfAdminFetch(`/api/rf-admin/users/${userId}/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ new_password }),
  });
  const body = await resp.json();
  if (!body.success) throw new Error(body.error?.message || "reset failed");
}